export const DASHA_SEQUENCE = [
  { planet: 'Ketu', years: 7 },
  { planet: 'Venus', years: 20 },
  { planet: 'Sun', years: 6 },
  { planet: 'Moon', years: 10 },
  { planet: 'Mars', years: 7 },
  { planet: 'Rahu', years: 18 },
  { planet: 'Jupiter', years: 16 },
  { planet: 'Saturn', years: 19 },
  { planet: 'Mercury', years: 17 }
];

const TOTAL_CYCLE_YEARS = 120;
const YEAR_MS = 365.25 * 24 * 60 * 60 * 1000;
const NAKSHATRA_SPAN_DEGREES = 360 / 27;

export interface DashaEntry {
  level: 'Mahadasha' | 'Antardasha' | 'Pratyantardasha';
  lord: string;
  periodYears: number;
  start: Date;
  end: Date;
  sequence: number;
  antardashas?: DashaEntry[];
  pratyantardashas?: DashaEntry[];
}

export interface RuntimeDashaResult {
  birthMahadashaLord: string;
  balanceYears: number;
  timezoneMinutes: number;
  mahadashas: DashaEntry[];
  currentMahadasha: DashaEntry | null;
  currentAntardasha: DashaEntry | null;
  currentPratyantardasha: DashaEntry | null;
}

const normalizeLongitude = (longitude: number) => ((longitude % 360) + 360) % 360;

const getPlanetYears = (planet: string) => {
  return DASHA_SEQUENCE.find(entry => entry.planet === planet)?.years || 0;
};

const rotateSequence = (startPlanet: string) => {
  const startIndex = DASHA_SEQUENCE.findIndex(entry => entry.planet === startPlanet);
  if (startIndex < 0) return DASHA_SEQUENCE;

  return [
    ...DASHA_SEQUENCE.slice(startIndex),
    ...DASHA_SEQUENCE.slice(0, startIndex)
  ];
};

export const parseTimezoneOffsetMinutes = (timezone: unknown): number => {
  if (typeof timezone === 'number' && Number.isFinite(timezone)) {
    return timezone;
  }

  if (typeof timezone === 'string') {
    const trimmed = timezone.trim();

    if (/^[+-]?\d+$/.test(trimmed)) {
      return Number(trimmed);
    }

    const match = trimmed.match(/^([+-]?)(\d{1,2}):(\d{2})$/);
    if (match) {
      const sign = match[1] === '-' ? -1 : 1;
      const hours = parseInt(match[2], 10);
      const minutes = parseInt(match[3], 10);
      return sign * ((hours * 60) + minutes);
    }
  }

  return 0;
};

const getBirthInstant = (chartData: any): Date | null => {
  const birthDateValue = chartData?.birthDetails?.dateOfBirth;
  const birthDate = new Date(birthDateValue);

  if (!birthDateValue || Number.isNaN(birthDate.getTime())) {
    return null;
  }

  const timeOfBirth = chartData?.birthDetails?.timeOfBirth || {};
  const timezoneMinutes = parseTimezoneOffsetMinutes(chartData?.birthDetails?.placeOfBirth?.timezone);

  const utcMillis = Date.UTC(
    birthDate.getUTCFullYear(),
    birthDate.getUTCMonth(),
    birthDate.getUTCDate(),
    Number(timeOfBirth.hour) || 0,
    Number(timeOfBirth.minute) || 0,
    Number(timeOfBirth.second) || 0
  ) - (timezoneMinutes * 60 * 1000);

  return new Date(utcMillis);
};

export const calculateRuntimeDasha = (chartData: any, asOfDate: Date = new Date()): RuntimeDashaResult | null => {
  const moonLongitude = chartData?.planets?.MOON?.longitude;
  const birthInstant = getBirthInstant(chartData);
  const timezoneMinutes = parseTimezoneOffsetMinutes(chartData?.birthDetails?.placeOfBirth?.timezone);

  if (moonLongitude === undefined || moonLongitude === null || !birthInstant) {
    return null;
  }

  const normalizedMoon = normalizeLongitude(moonLongitude);
  const nakshatraIndex = Math.floor(normalizedMoon / NAKSHATRA_SPAN_DEGREES);
  const birthMahadashaLord = DASHA_SEQUENCE[nakshatraIndex % DASHA_SEQUENCE.length].planet;
  const birthMahadashaYears = getPlanetYears(birthMahadashaLord);

  const degreesTraversedInNakshatra = normalizedMoon - (nakshatraIndex * NAKSHATRA_SPAN_DEGREES);
  const degreesRemainingInNakshatra = NAKSHATRA_SPAN_DEGREES - degreesTraversedInNakshatra;
  const remainingFraction = degreesRemainingInNakshatra / NAKSHATRA_SPAN_DEGREES;
  const balanceYears = birthMahadashaYears * remainingFraction;
  const elapsedYears = birthMahadashaYears - balanceYears;

  let mahadashaStart = new Date(birthInstant.getTime() - (elapsedYears * YEAR_MS));

  const mahadashas: DashaEntry[] = [];
  let currentMahadasha: DashaEntry | null = null;
  let currentAntardasha: DashaEntry | null = null;
  let currentPratyantardasha: DashaEntry | null = null;

  rotateSequence(birthMahadashaLord).forEach((mahadasha, mdIndex) => {
    const mdYears = mahadasha.years;
    const mdEnd = new Date(mahadashaStart.getTime() + (mdYears * YEAR_MS));

    const mahadashaEntry: DashaEntry = {
      level: 'Mahadasha',
      lord: mahadasha.planet,
      periodYears: mdYears,
      start: new Date(mahadashaStart),
      end: mdEnd,
      sequence: mdIndex + 1,
      antardashas: []
    };

    let antardashaStart = new Date(mahadashaStart);

    rotateSequence(mahadasha.planet).forEach((antardasha, adIndex) => {
      const adYears = (mdYears * antardasha.years) / TOTAL_CYCLE_YEARS;
      const adEnd = new Date(antardashaStart.getTime() + (adYears * YEAR_MS));

      const antardashaEntry: DashaEntry = {
        level: 'Antardasha',
        lord: antardasha.planet,
        periodYears: adYears,
        start: new Date(antardashaStart),
        end: adEnd,
        sequence: adIndex + 1,
        pratyantardashas: []
      };

      let pratyantardashaStart = new Date(antardashaStart);

      rotateSequence(antardasha.planet).forEach((pratyantardasha, pdIndex) => {
        const pdYears = (mdYears * antardasha.years * pratyantardasha.years) / (TOTAL_CYCLE_YEARS * TOTAL_CYCLE_YEARS);
        const pdEnd = new Date(pratyantardashaStart.getTime() + (pdYears * YEAR_MS));

        const pratyantardashaEntry: DashaEntry = {
          level: 'Pratyantardasha',
          lord: pratyantardasha.planet,
          periodYears: pdYears,
          start: new Date(pratyantardashaStart),
          end: pdEnd,
          sequence: pdIndex + 1
        };

        if (asOfDate >= pratyantardashaEntry.start && asOfDate < pratyantardashaEntry.end) {
          currentPratyantardasha = pratyantardashaEntry;
        }

        antardashaEntry.pratyantardashas?.push(pratyantardashaEntry);
        pratyantardashaStart = pdEnd;
      });

      if (asOfDate >= antardashaEntry.start && asOfDate < antardashaEntry.end) {
        currentAntardasha = antardashaEntry;
      }

      mahadashaEntry.antardashas?.push(antardashaEntry);
      antardashaStart = adEnd;
    });

    if (asOfDate >= mahadashaEntry.start && asOfDate < mahadashaEntry.end) {
      currentMahadasha = mahadashaEntry;
    }

    mahadashas.push(mahadashaEntry);
    mahadashaStart = mdEnd;
  });

  return {
    birthMahadashaLord,
    balanceYears,
    timezoneMinutes,
    mahadashas,
    currentMahadasha,
    currentAntardasha,
    currentPratyantardasha
  };
};

const shiftToTimezone = (date: Date, timezoneMinutes: number) => {
  return new Date(date.getTime() + (timezoneMinutes * 60 * 1000));
};

export const formatDashaDate = (date: Date, timezoneMinutes = 0) => {
  return shiftToTimezone(date, timezoneMinutes).toLocaleDateString('en-GB', {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

export const formatDashaTime = (date: Date, timezoneMinutes = 0) => {
  return shiftToTimezone(date, timezoneMinutes).toLocaleTimeString('en-GB', {
    timeZone: 'UTC',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

export const formatDashaYears = (periodYears: number) => {
  const totalDays = periodYears * 365.25;
  const years = Math.floor(totalDays / 365.25);
  const remainingDaysAfterYears = totalDays % 365.25;
  const months = Math.floor(remainingDaysAfterYears / 30.4375);
  const days = Math.round(remainingDaysAfterYears % 30.4375);

  if (years > 0) return `${years}y ${months}m ${days}d`;
  if (months > 0) return `${months}m ${days}d`;
  return `${days}d`;
};
